import { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material'
import { mcpApi } from '../services/mcpApi'

/**
 * Bridge route used in the MCP/OAuth flow.
 *
 * Backend's /mcp/oauth/authorize redirects here when the mcp_session cookie
 * is missing. This page calls /mcp/oauth/init-session (which sets the cookie
 * for the current domain), then redirects back to the original authorize URL
 * passed in `?return_to=...`.
 *
 * If the user is not authenticated, App.jsx will render <Login /> first
 * because of the wildcard route — after successful login App re-renders the
 * authenticated branch and this component runs.
 */
function normalizeReturnTo(rawReturnTo) {
  if (!rawReturnTo) return null
  try {
    const u = new URL(rawReturnTo)
    // If the page itself is on HTTPS but return_to has http://, upgrade it.
    // Backend behind nginx sees scheme=http (TLS terminated upstream); even
    // when X-Forwarded-Proto handling is fixed, this guard prevents redirect
    // loops while infra/code rolls out.
    if (window.location.protocol === 'https:' && u.protocol === 'http:' && u.host === window.location.host) {
      u.protocol = 'https:'
    }
    return u.toString()
  } catch (_) {
    return rawReturnTo
  }
}

export default function McpConnectPage() {
  const [error, setError] = useState(null)
  const params = new URLSearchParams(window.location.search)
  const returnTo = normalizeReturnTo(params.get('return_to'))

  useEffect(() => {
    if (!returnTo) {
      setError('Brak parametru return_to — nie wiem dokąd Cię odesłać.')
      return
    }

    // Use current window origin for init-session (matches the cookie domain
    // and protocol the browser is currently on — avoids mixed-content errors
    // when backend's request.url has http while frontend is https).
    const baseUrl = window.location.origin

    mcpApi
      .initSession(baseUrl)
      .then(() => {
        window.location.replace(returnTo)
      })
      .catch((err) => {
        setError(
          err.response?.data?.detail ||
            err.message ||
            'Nie udało się zainicjalizować sesji MCP.'
        )
      })
  }, [returnTo])

  return (
    <Box className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6">
      {!error ? (
        <>
          <CircularProgress />
          <Typography variant="body2" className="text-gray-600">
            Łączę Cię z autoryzacją MCP…
          </Typography>
        </>
      ) : (
        <>
          <Alert severity="error" className="max-w-lg">
            <strong>Błąd:</strong> {error}
          </Alert>
          <Button variant="outlined" onClick={() => window.location.replace('/dashboard')}>
            Wróć do dashboardu
          </Button>
        </>
      )}
    </Box>
  )
}
